import { STORE_NAME } from '@intigral/constant';
import { Button, FontIcon, Spinner } from '@intigral/shared';
import { log } from '@intigral/utils';
import { get } from '@intigral/utils/lib/browser-storage/localStorage';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { ButtonTypes } from '../../config/constants';
import { __ } from '../../translations';
import { getUserInfo } from '../../utils/user';
import { useConfigurationSections } from '../../hooks/useConfiguration';
import { IStore } from '../../interfaces/StoreTypes';
import { FontIconSize } from '@intigral/shared/components/FontIcon';
import { useUserWatchList } from '../../hooks/watchlist/useUserWatchlist';
import { useToggleWatchlistItem } from '../../hooks/watchlist/useToggleWatchlist';
import user from '../../helpers/user';
import { showGuestPromotionPopup } from '../../config/guestMode';

interface AddWatchlistButtonProps {
    componentType?: string;
    componentName?: any;
    type?: string;
    item?: { paId?: string; Id?: string; programType?: string; category?: string };
    onRemove?: () => void;
    onClick?: (isAdded?: boolean) => void;
    watchlistButton?: boolean;
    children?: React.ReactNode;
}

const AddWatchlistButton: React.FC<AddWatchlistButtonProps> = ({
    type,
    item,
    onRemove,
    onClick,
    watchlistButton = false,
    children,
}) => {
    let paId = null;
    if (type === ButtonTypes.LinearItem || type === ButtonTypes.ProgramItem) paId = item?.paId;
    else paId = item?.Id;
    const [optimisticMap, setOptimisticMap] = useState<Record<string, boolean>>({});
    const geolocation = useSelector((store: IStore) => store.location);
    const { promotionProviderImages } = useConfigurationSections();
    const providers = promotionProviderImages;
    const selectedProfile = get(STORE_NAME.SELECTED_PROFILE);

    const { data } = useUserWatchList(
        selectedProfile?.profileGuid,
        getUserInfo(),
        geolocation.countryCode,
        providers,
        'en',
        ''
    );
    const toggleWatchlistItemMutation = useToggleWatchlistItem();

    const [isSaving, setIsSaving] = useState<boolean>(false);
    const isAdded = optimisticMap[paId] ?? data?.some((e: any) => e.Id === paId);

    const addOrRemoveWatchlistMutation = (e) => {
        e.stopPropagation();
        if (showGuestPromotionPopup(e)) {
            return;
        }
        e.preventDefault();
        const nextValue = !isAdded;
        setOptimisticMap((prev) => ({
            ...prev,
            [paId]: nextValue,
        }));
        toggleWatchlistItemMutation.mutate(
            {
                itemId: paId,
                category: (item?.category?.toLowerCase() ?? '') as 'linear' | 'vod',
                isAdded,
            },
            {
                onError: () => {
                    setOptimisticMap((prev) => {
                        const copy = { ...prev };
                        delete copy[paId];
                        return copy;
                    });
                },
            }
        );
        if (onClick) {
            onClick(isAdded);
        }
    };

    /**
     * Close or cross icon depending on whether item was added to watchlist
     * @param {string} size Setting different icon size depending on whether it's used in promo banner
     * or in popup in carousel
     */
    const getWatchlistStatusIcon = (size: FontIconSize = 'small') => {
        return <FontIcon name={isAdded ? 'close' : 'add'} size={size} md={size} />;
    };

    const defaultProps = {
        tabIndex: -1,
        onClick: addOrRemoveWatchlistMutation,
    };

    switch (type) {
        case ButtonTypes.VodFeaturedItem:
            return (
                <Button
                    social
                    secondary
                    {...defaultProps}
                    // loading={toggleWatchlistItemMutation.isPending}
                    data-test-id="watchlist-button"
                >
                    {getWatchlistStatusIcon('smaller')}
                </Button>
            );
        case ButtonTypes.LinearItem:
            return (
                <span
                    id="add-watch-list"
                    data-test-id="add-watch-list"
                    className={`add-icon ${isAdded ? 'remove-icon' : ''} ${isSaving ? 'watch-loading' : ''}`}
                    {...defaultProps}
                >
                    <div>
                        <FontIcon name="cross" size="smaller" md="medium" />
                        {/* {!toggleWatchlistItemMutation.isPending ? (
                            <FontIcon name="cross" size="smaller" md="medium" />
                        ) : (
                            <div className="spinner">
                                <Spinner inline />
                            </div>
                        )} */}
                    </div>
                </span>
            );
        case ButtonTypes.VodDetails:
        case ButtonTypes.ProgramItem:
            return (
                <Button
                    secondary={watchlistButton ? true : false}
                    compact
                    className={`mylist-btn ${isAdded ? 'remove-mylist' : ''}  ${isSaving ? 'watch-loading' : ''}`}
                    // loading={toggleWatchlistItemMutation.isPending}
                    data-test-id="watchlist-button"
                    {...defaultProps}
                >
                    <span>{watchlistButton ? __('mylist_button_label') : __('home_carousel_watch_list')}</span>
                    {getWatchlistStatusIcon('small')}
                </Button>
            );
        case ButtonTypes.RewindListProgramItem:
            return (
                <Button
                    secondary={watchlistButton ? true : false}
                    compact
                    className={`mylist-btn ${isAdded ? 'remove-mylist' : ''}  ${isSaving ? 'watch-loading' : ''}`}
                    // loading={toggleWatchlistItemMutation.isPending}
                    data-test-id="watchlist-button"
                    {...defaultProps}
                >
                    <span>{watchlistButton ? __('mylist_button_label') : __('home_carousel_watch_list')}</span>
                    {getWatchlistStatusIcon('smaller')}
                </Button>
            );

        case ButtonTypes.VodeItem:
            return (
                <Button
                    {...{ [children ? 'fullWidth' : 'social']: true }}
                    secondary
                    compact
                    // loading={toggleWatchlistItemMutation.isPending}
                    data-test-id="watchlist-button"
                    {...defaultProps}
                >
                    {children ?? null} {getWatchlistStatusIcon('smaller')}
                </Button>
            );
        case 'VodItem-details-overlay':
            return (
                <Button
                    plain
                    // loading={toggleWatchlistItemMutation.isPending}
                    data-test-id="watchlist-button"
                    {...defaultProps}
                >
                    <FontIcon name="cross-circle" size="small" md="small" rotate={isAdded ? 'partial' : null} />
                </Button>
            );

        case ButtonTypes.EpisodeItem:
        case ButtonTypes.RewindItem:
            return (
                <Button
                    plain
                    className={`add-icon ${isAdded ? 'remove-icon' : ''} ${isSaving ? 'watch-loading' : ''}`}
                    data-test-id="watchlist-button"
                    {...defaultProps}
                >
                    <div style={{ transform: 'scale(1.1)' }}>
                        <FontIcon name="cross-circle" size="x-large" md="size60" rotate={isAdded ? 'partial' : null} />
                    </div>
                </Button>
            );
        default:
            return (
                <button
                    className={`add-icon ${isAdded ? 'remove-icon' : ''} ${isSaving ? 'watch-loading' : ''}`}
                    data-test-id="watchlist-button"
                    {...defaultProps}
                >
                    <div className="circle3"> </div>
                </button>
            );
    }
};
export default AddWatchlistButton;
